import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { LAYER_PALETTE, CONFLICT_PALETTE, STRUCTURE_GLYPH, readLayerVisibility, writeLayerVisibility } from "@/lib/operationalPalette";

/**
 * LayerOverlay — renders operational structure markers ON TOP of the
 * drawing, inside the canvas's transformed wrapper so markers inherit
 * pan/zoom and stay spatially locked through tile fade-ins.
 *
 * SVG is positioned absolutely with viewBox 0..100 and
 * preserveAspectRatio="none" so a (normalized_x, normalized_y) translates
 * directly to (% width, % height). Restrained palette, no chrome.
 *
 * Tap behaviour: the marker is the touch target with a forgiving radius;
 * onPick(structure) opens the inspector. A subtle pulse animation runs on
 * the focused marker until inspector closes.
 *
 * Props:
 *   sheetId   — current sheet
 *   imgDims   — natural dims of the base raster (used for SVG width/height)
 *   focusedId — id of the currently inspected structure
 *   onPick(structure) — callback when a marker is tapped
 */
export default function LayerOverlay({ sheetId, imgDims, focusedId, onPick }) {
  const [layers, setLayers] = useState([]);
  const [structures, setStructures] = useState([]);
  // Per-sheet visibility map { layer_type: bool }. Restored from
  // localStorage so the user's selection survives navigation.
  const [visibility, setVisibility] = useState({});

  useEffect(() => {
    if (!sheetId) return;
    let cancelled = false;
    Promise.all([
      api.get(`/sheets/${sheetId}/layers`).catch(() => ({ data: [] })),
      api.get(`/sheets/${sheetId}/structures`).catch(() => ({ data: [] })),
    ]).then(([L, S]) => {
      if (cancelled) return;
      const ls = L.data || [];
      const ss = S.data || [];
      setLayers(ls);
      setStructures(ss);
      const stored = readLayerVisibility(sheetId);
      const fresh = {};
      for (const l of ls) {
        fresh[l.layer_type] = stored?.[l.layer_type] !== undefined
          ? !!stored[l.layer_type]
          : !!l.default_visible;
      }
      setVisibility(fresh);
    });
    return () => { cancelled = true; };
  }, [sheetId]);

  // Persist visibility on every change.
  useEffect(() => {
    if (sheetId && Object.keys(visibility).length) {
      writeLayerVisibility(sheetId, visibility);
    }
  }, [visibility, sheetId]);

  const visibleStructures = useMemo(
    () => structures.filter((s) => visibility[s.layer_type]),
    [structures, visibility],
  );

  if (!sheetId || !imgDims?.w || !structures.length) {
    // Still expose the panel — empty overlay is fine.
    return <LayerPanelOnly layers={layers} visibility={visibility} setVisibility={setVisibility} />;
  }

  return (
    <>
      <svg
        className="absolute inset-0 pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        data-testid="layer-overlay"
      >
        {visibleStructures.map((s) => (
          <StructureMarker
            key={s.id}
            structure={s}
            focused={focusedId === s.id}
            onPick={onPick}
          />
        ))}
      </svg>
      <LayerPanelOnly layers={layers} visibility={visibility} setVisibility={setVisibility} />
    </>
  );
}


function LayerPanelOnly({ layers, visibility, setVisibility }) {
  if (!layers.length) return null;
  // Portal to <body> so `position: fixed` is resolved against the viewport
  // rather than the SheetCanvas's transformed stage (transform creates a
  // containing block for fixed descendants). This also keeps the panel's
  // pointer events isolated from the canvas wrap, so clicks toggle cleanly.
  if (typeof document === "undefined") return null;
  return createPortal(
    <LayersPanel
      layers={layers}
      visibility={visibility}
      onToggle={(layer_type) =>
        setVisibility((v) => ({ ...v, [layer_type]: !v[layer_type] }))
      }
    />,
    document.body,
  );
}


/**
 * StructureMarker — small SVG glyph per structure type. All glyphs render
 * inside a 4×4 unit footprint at (cx, cy) = (normalized_x, normalized_y) *
 * 100 so they appear roughly tap-friendly even at fit-scale. Tap radius is
 * widened by an invisible hit-rect.
 */
function StructureMarker({ structure, focused, onPick }) {
  const cx = structure.normalized_x * 100;
  const cy = structure.normalized_y * 100;
  const palette = LAYER_PALETTE[structure.layer_type] || LAYER_PALETTE.storm;
  const conflict = CONFLICT_PALETTE[structure.conflict_state] || CONFLICT_PALETTE.normal;
  const glyph = STRUCTURE_GLYPH[structure.structure_type] || "circle";
  // Wave B · Turn 2 — a structure carried forward from a prior revision
  // lands as `lineage_status="pending_confirmation"` and must never look
  // as authoritative as a confirmed one until a human confirms it.
  const pending = structure.lineage_status === "pending_confirmation";

  return (
    <g
      style={{ cursor: "pointer", pointerEvents: "auto", opacity: pending ? 0.55 : 1 }}
      onPointerDown={(e) => {
        // Stop the canvas's pointerdown handler from firing — otherwise it
        // calls setPointerCapture on the wrap div, which redirects the
        // subsequent click event away from this marker and into a pan
        // gesture (Issue 1, Iter 31). Without this guard the inspector
        // never opens because <g>.onClick is never delivered.
        e.stopPropagation();
      }}
      onClick={(e) => { e.stopPropagation(); onPick && onPick(structure); }}
      data-testid={`structure-pin-${structure.id}`}
      data-marker-id={structure.id}
      data-structure-id={structure.id}
      data-structure-label={structure.structure_label}
      data-conflict={structure.conflict_state}
      data-lineage-status={structure.lineage_status || "native"}
    >
      {pending && <title>{`Carried from prior revision — unconfirmed`}</title>}
      {/* Forgiving touch target — invisible 5×5 unit rect (≈48px at fit
          scale on a 1080p viewport, plenty for gloved fingers). */}
      <rect x={cx - 2.5} y={cy - 2.5} width="5" height="5" fill="transparent" />

      {/* Conflict ring — restrained outer outline, only rendered when
          conflict_state != normal. */}
      {conflict.ring && (
        <circle
          cx={cx} cy={cy} r={focused ? 2.4 : 2.0}
          fill="none"
          stroke={conflict.ring}
          strokeWidth={focused ? 0.45 : 0.32}
          vectorEffect="non-scaling-stroke"
          opacity={0.95}
        >
          {focused && (
            <animate attributeName="r" values="2.0;2.6;2.0" dur="1.6s" repeatCount="indefinite" />
          )}
        </circle>
      )}

      {/* Unconfirmed lineage ring — dashed amber outline so a carried
          -forward-but-not-yet-confirmed structure is visually distinct
          from a confirmed / native one, even before it's tapped open. */}
      {pending && (
        <circle
          cx={cx} cy={cy} r={2.2}
          fill="none"
          stroke="#f59e0b"
          strokeDasharray="0.4,0.4"
          strokeWidth={0.3}
          vectorEffect="non-scaling-stroke"
        />
      )}

      {/* Glyph */}
      <Glyph kind={glyph} cx={cx} cy={cy} palette={palette} focused={focused} />

      {/* Structure label — tiny tag below glyph */}
      <text
        x={cx}
        y={cy + 2.3}
        fontSize="1.1"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
        fontWeight="700"
        textAnchor="middle"
        fill="#0a0a0b"
        stroke={palette.stroke}
        strokeWidth="0.06"
        paintOrder="stroke"
        style={{ userSelect: "none", pointerEvents: "none" }}
      >
        {structure.structure_label}{pending ? " · UNCONFIRMED" : ""}
      </text>
    </g>
  );
}


function Glyph({ kind, cx, cy, palette, focused }) {
  const sw = focused ? 0.32 : 0.22;
  const stroke = palette.stroke;
  const fill = palette.fill;
  switch (kind) {
    case "circle":
      return (
        <g>
          <circle cx={cx} cy={cy} r="1.0" fill={fill} stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
          <line x1={cx - 0.5} y1={cy} x2={cx + 0.5} y2={cy} stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
          <line x1={cx} y1={cy - 0.5} x2={cx} y2={cy + 0.5} stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
        </g>
      );
    case "rect":
      return (
        <rect x={cx - 0.9} y={cy - 0.9} width="1.8" height="1.8" fill={fill} stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
      );
    case "rect-amber":
      return (
        <rect x={cx - 0.85} y={cy - 0.85} width="1.7" height="1.7" rx="0.2" fill={fill} stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
      );
    case "diamond":
      return (
        <polygon points={`${cx},${cy-1.1} ${cx+1.0},${cy} ${cx},${cy+1.1} ${cx-1.0},${cy}`} fill={fill} stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
      );
    case "valve":
      // Bowtie — two triangles meeting at centre.
      return (
        <g>
          <polygon points={`${cx-1.1},${cy-0.8} ${cx},${cy} ${cx-1.1},${cy+0.8}`} fill={fill} stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
          <polygon points={`${cx+1.1},${cy-0.8} ${cx},${cy} ${cx+1.1},${cy+0.8}`} fill={fill} stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
        </g>
      );
    case "trapezoid":
      return (
        <polygon points={`${cx-1.2},${cy+0.7} ${cx-0.6},${cy-0.7} ${cx+0.6},${cy-0.7} ${cx+1.2},${cy+0.7}`} fill={fill} stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
      );
    default:
      return <circle cx={cx} cy={cy} r="0.8" fill={fill} stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />;
  }
}


/**
 * LayersPanel — restrained left rail with toggleable layer chips.
 * Field-readable: short labels, large hit areas, persistent state.
 * Mounts as a position:fixed sibling of the canvas (NOT inside the
 * transformed wrapper), so it doesn't pan with the drawing.
 */
function LayersPanel({ layers, visibility, onToggle }) {
  return (
    <div
      className="fixed left-3 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-1.5 p-2 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)]/95 backdrop-blur-sm shadow-xl"
      data-testid="layers-panel"
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] px-1 pb-1 border-b border-[var(--fop-border)]/60">
        Layers
      </div>
      {layers.map((l) => {
        const palette = LAYER_PALETTE[l.layer_type] || {};
        const visible = !!visibility[l.layer_type];
        return (
          <button
            key={l.id}
            onClick={() => onToggle(l.layer_type)}
            data-testid={`layer-toggle-${l.layer_type}`}
            aria-pressed={visible}
            className={`flex items-center gap-2 min-h-[44px] px-3 rounded text-xs font-mono uppercase tracking-wider transition-colors min-w-[160px] ${
              visible
                ? "bg-[var(--fop-surface-2)] text-[var(--fop-text)] border border-[var(--fop-border)]"
                : "text-[var(--fop-text-2)] border border-transparent hover:border-[var(--fop-border)]"
            }`}
            title={`Toggle ${l.label}`}
          >
            <span
              className="inline-block w-3 h-3 rounded-sm border"
              style={{
                background: visible ? palette.swatch : "transparent",
                borderColor: palette.swatch,
              }}
            />
            <span className="flex-1 text-left">{l.label}</span>
            <span className="text-[10px] text-[var(--fop-text-3)] tracking-widest">
              {visible ? "ON" : "OFF"}
            </span>
          </button>
        );
      })}
    </div>
  );
}
